import React from "react";
import { Link } from "react-router-dom";
import {
  Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from "recharts";
import { Activity, ArrowRight, Mail, MessageSquare, Wifi } from "lucide-react";

const numberFormatter = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const formatDate = (value, compact = false) => new Intl.DateTimeFormat("en-GB", compact
  ? { day: "numeric", month: "short" }
  : { weekday: "short", day: "numeric", month: "short" }).format(new Date(`${value}T12:00:00`));

function ActivityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="home-activity-tooltip">
      <strong>{formatDate(label)}</strong>
      {payload.map((item) => <span key={item.dataKey} style={{ color: item.color }}>{item.name}: {numberFormatter.format(item.value)}</span>)}
    </div>
  );
}

const SUMMARY_ITEMS = [
  { key: "connections", label: "Guest connections", detail: "Wi-Fi sessions", icon: Wifi, to: "/guest-wifi/connections", color: "violet" },
  { key: "emailOpens", label: "Emails opened", detail: "Campaign engagement", icon: Mail, to: "/marketing/analytics", color: "teal" },
  { key: "reviews", label: "New reviews", detail: "Guest feedback", icon: MessageSquare, to: "/reviews/reviews", color: "amber" },
];

export default function HomeActivityPulse({ activity, periodLabel }) {
  const hasMeasuredActivity = Object.values(activity.totals).some((value) => Number(value) > 0);
  return (
    <section className="home-activity-section" aria-labelledby="home-activity-title" aria-busy={activity.isLoading}>
      <div className="home-section-heading">
        <div><h2 id="home-activity-title"><Activity size={19} strokeWidth={1.7} /> Activity pulse</h2><p>How guests moved through your connected tools during {periodLabel.toLowerCase()}.</p></div>
        <div className="home-activity-heading-actions">
          <div className="home-activity-legend" aria-label="Chart legend">
            <span className="is-violet">Connections</span><span className="is-teal">Email opens</span><span className="is-amber">Reviews</span>
          </div>
        </div>
      </div>
      <div className="home-activity-layout">
        <div className="home-activity-chart" aria-label={`Activity chart for ${periodLabel}`}>
          {activity.isLoading && !hasMeasuredActivity ? (
            <div className="home-activity-empty home-activity-loading" role="status"><Activity size={25} /><strong>Gathering your activity…</strong><span>Bringing your connected tools into one view.</span></div>
          ) : !hasMeasuredActivity ? (
            <div className="home-activity-empty"><Activity size={25} /><strong>Your activity will build here.</strong><span>Connections, email opens and reviews will appear as they happen.</span></div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={activity.series} margin={{ top: 12, right: 15, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="homeConnectionsFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6d45e8" stopOpacity={0.28} /><stop offset="95%" stopColor="#6d45e8" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="var(--gray-a4)" />
                <XAxis dataKey="date" tickFormatter={(value) => formatDate(value, true)} axisLine={false} tickLine={false} tick={{ fill: "var(--gray-10)", fontSize: 10 }} minTickGap={24} />
                <YAxis yAxisId="connections" allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "var(--gray-10)", fontSize: 10 }} />
                <YAxis yAxisId="engagement" orientation="right" allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "var(--gray-10)", fontSize: 10 }} />
                <Tooltip content={<ActivityTooltip />} cursor={{ stroke: "var(--gray-a7)", strokeDasharray: "3 3" }} />
                <Area yAxisId="connections" type="monotone" dataKey="connections" name="Connections" stroke="#6d45e8" strokeWidth={2} fill="url(#homeConnectionsFill)" activeDot={{ r: 4 }} />
                <Line yAxisId="engagement" type="monotone" dataKey="emailOpens" name="Email opens" stroke="#0d9488" strokeWidth={2} dot={activity.series.length <= 7} activeDot={{ r: 4 }} />
                <Line yAxisId="engagement" type="monotone" dataKey="reviews" name="Reviews" stroke="#d97706" strokeWidth={2} dot={activity.series.length <= 7} activeDot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="home-activity-summary" aria-label="Activity summary">
          <div className="home-activity-summary-heading"><span>Across your tools</span><small>{periodLabel}</small></div>
          {activity.isError && <div className="home-activity-partial" role="status">Some activity is temporarily unavailable.</div>}
          {SUMMARY_ITEMS.map((item) => {
            const Icon = item.icon;
            const value = activity.totals[item.key];
            return <Link to={item.to} className="home-activity-item" key={item.key}>
              <span className={`home-activity-item-icon is-${item.color}`}><Icon size={17} /></span>
              <span><strong>{value == null ? "—" : numberFormatter.format(value)}</strong><small>{item.label}<em>{item.detail}</em></small></span>
              <ArrowRight size={14} />
            </Link>;
          })}
          <p className="home-activity-note">Each metric keeps its natural scale in the chart so smaller signals remain visible.</p>
        </div>
      </div>
    </section>
  );
}
